import type { Lens, LensCardType, LensDataset, LensPlan, LensPlanAnchor } from '~/types/leLens';

import type { ConfiguredTableOutput } from './catalogue/catalogueLensStart';

/**
 * The compact catalogue shape POST /api/reporting/le-lenses/plan accepts
 * alongside a brief: a list of datasets the caller can already read, kept to
 * exactly the fields the planner needs so the request stays small. Field
 * names match that request contract so the client can forward this array
 * without reshaping it.
 */
export type PlanCatalogueEntry = {
  /**
   * Unique across the whole catalogue, not just within one dashboard's spec.
   * A LensDataset's own id is a short slug that only resolves inside the
   * Lens that declared it, so two different dashboards can and do reuse the
   * same slug for unrelated datasets; this id is `${lensId}:${datasetId}`
   * to keep every entry in the catalogue distinct.
   */
  id: string;
  name: string;
  source: string;
  grain: string;
  columns: Array<string>;
};

/**
 * Shown on every candidate in the "Start from your work" lane. Not
 * Certified: certification is a claim about a governed metric registry that
 * does not exist yet, so a dataset pulled from an existing dashboard is
 * labelled by where it came from rather than a status nothing here can
 * verify.
 */
export const WORKSPACE_BADGE_LABEL = 'From this workspace' as const;

/**
 * One dataset from a dashboard the viewer can already read, carrying the
 * compact catalogue fields plus everything a candidate card renders and
 * everything the plan gate needs to reuse it.
 */
export type WorkspaceCandidate = PlanCatalogueEntry & {
  lensId: string;
  lensTitle: string;
  datasetId: string;
  badge: typeof WORKSPACE_BADGE_LABEL;
  /**
   * The original dataset definition, carried whole rather than left as an
   * id: a dataset id only resolves inside the Lens spec that declared it, so
   * building a new Lens from this candidate means copying this object into
   * its spec.datasets, not referencing the dashboard it came from.
   */
  dataset: LensDataset;
};

/**
 * What this screen hands to the plan gate. Neither branch is built, run or
 * saved by this screen. 'selection' already names the datasets the gate
 * should bind cards to directly, since the user chose them by hand and there
 * is nothing left to resolve. 'brief' is the exact request shape
 * POST /api/reporting/le-lenses/plan takes, so the gate can call it as is.
 */
export type CreateJourneyStart =
  | {
      kind: 'selection';
      candidates: Array<WorkspaceCandidate>;
      /** What the person had typed in the brief box before picking a dataset by hand instead.
       *  Used only to seed the new Lens's title (`defaultDashboardTitle`); the picked candidates,
       *  not this text, still decide what the Lens actually reads. */
      titleSeed?: string;
    }
  | {
      kind: 'brief';
      brief: string;
      /** Optional native presentation requested by a curated surface. */
      cardTypeHint?: LensCardType;
      /** Exact governed grouping field selected on a curated analysis. */
      viewXHint?: string;
      configuredTable?: ConfiguredTableOutput;
      /** The person's own question or outcome, shown in the review UI. `brief` may also carry
       *  answer evidence and planner instructions that should remain internal. */
      objective?: string;
      catalogue: Array<PlanCatalogueEntry>;
      /** Set when a governed dataset is available, so the plan answers from it rather than
       *  writing SQL: passed straight through to POST .../plan. */
      anchor?: LensPlanAnchor;
      /** The anchor's own dataset, ready to save as this new Lens's first dataset, before any
       *  card is approved. Always present together with `anchor`. */
      anchorDataset?: LensDataset;
      /**
       * True when no single anchor is known yet, so the server should pick a governed dataset
       * per card across the whole catalogue instead of writing SQL for every card. Mutually
       * exclusive with `anchor` in practice: a brief that already names a fixed anchor plans
       * against that one dataset, and a brief with neither routes nor anchors falls to the
       * ordinary unanchored, SQL-generating plan.
       */
      route?: boolean;
      preplanned?: LensPlan;
      /** The stored plan this journey opened, carried to the create call for funnel attribution. */
      planId?: string;
      /** How far a prepared plan was already settled away from this screen. 'sources' means the
       *  cards were reviewed wherever the plan was prepared, so the gate opens on source mapping. */
      preplannedStep?: 'cards' | 'sources';
      /**
       * True when the brief was built from an answer the reader already has, rather than typed
       * from scratch. The plan endpoint reads it as a diagnosis to keep rather than a board to
       * start, since the question behind it has already been answered once.
       */
      fromAnswer?: boolean;
    };

/**
 * What the plan gate does once a plan is approved: merge the approved cards, datasets and
 * sections into a Lens that already exists.
 *
 * Creating a brand-new Lens is no longer this gate's job (02_CONVERSATIONAL_CREATE_SCOPE.md,
 * "every path that creates a Lens goes through chat"; phase3/F2_DOORS.md, door 10): every caller
 * that used to pass `{kind: 'create'}` now starts a chat conversation instead. Kept as a one-member
 * discriminated type, not a bare object, so a future second target kind (should one arise) still
 * cannot be passed as this one by accident.
 */
export type PlanGateTarget = { kind: 'append'; lens: Lens; onAppended: () => void };
